import React, { useState } from "react";
import ProgressBar from "react-bootstrap/ProgressBar";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import { Link } from "react-router-dom";
import { FiPlus, FiLink, FiTrash2, FiArrowRight } from "react-icons/fi";
import { slugify } from "../../utils/slug";
import { topicEmoji } from "../../utils/topicMeta";
import { parseProblemUrl } from "../../utils/parseProblemUrl";

export default function TopicCard({ questionData, addTopic, deleteTopic, addProblem }) {
  const [showAdd, setShowAdd] = useState(false);
  const [newTopic, setNewTopic] = useState("");
  const [addError, setAddError] = useState("");

  // ---- "Add problem from a link" modal state ----
  const emptyProblem = { URL: "", Problem: "", topicSlug: "", Difficulty: "", URL2: "" };
  const [showAddProblem, setShowAddProblem] = useState(false);
  const [problemForm, setProblemForm] = useState(emptyProblem);
  const [problemSource, setProblemSource] = useState("");
  const [problemError, setProblemError] = useState("");
  const [titleEdited, setTitleEdited] = useState(false);

  // Utility func() to find the progress in percentage
  const findPercentage = (doneQuestions, totalQuestions) => {
    if (!totalQuestions) return 0;
    return Math.round((doneQuestions / totalQuestions) * 100);
  };

  function handleAddTopic() {
    if (!newTopic.trim()) {
      setAddError("Please enter a topic name.");
      return;
    }
    addTopic(newTopic, (res) => {
      if (res && res.ok) {
        setNewTopic("");
        setAddError("");
        setShowAdd(false);
      } else if (res && res.reason === "exists") {
        setAddError("A topic with that name already exists.");
      } else {
        setAddError("Please enter a valid topic name.");
      }
    });
  }

  function handleDeleteTopic(e, topic) {
    e.preventDefault();
    e.stopPropagation();
    if (window.confirm(`Delete custom topic "${topic.topicName}" and all its problems?`)) {
      deleteTopic(slugify(topic.topicName));
    }
  }

  // ---- Add-problem-from-a-link handlers ----

  function openAddProblem() {
    // default the topic to the first one so the user can add with one click
    const firstSlug = questionData.length ? slugify(questionData[0].topicName) : "";
    setProblemForm({ ...emptyProblem, topicSlug: firstSlug });
    setProblemSource("");
    setProblemError("");
    setTitleEdited(false);
    setShowAddProblem(true);
  }

  // When the user pastes/types a URL we auto-fill the title + detected source,
  // unless they have manually edited the title field.
  function handleProblemUrlChange(value) {
    const parsed = parseProblemUrl(value);
    setProblemSource(parsed.source);
    setProblemForm((prev) => ({
      ...prev,
      URL: value,
      Problem: titleEdited ? prev.Problem : parsed.problem,
    }));
  }

  function submitAddProblem() {
    const problemName = problemForm.Problem.trim();
    if (!problemForm.URL.trim() && !problemName) {
      setProblemError("Paste a problem link (or at least type a name).");
      return;
    }
    if (!problemName) {
      setProblemError("Couldn't read a title from that link — please type one.");
      return;
    }
    if (!problemForm.topicSlug) {
      setProblemError("Pick a topic to add this problem to.");
      return;
    }
    addProblem(
      problemForm.topicSlug,
      {
        Problem: problemName,
        URL: problemForm.URL.trim(),
        URL2: problemForm.URL2.trim(),
        Difficulty: problemForm.Difficulty,
      },
      () => {
        setShowAddProblem(false);
        setProblemForm(emptyProblem);
        setProblemSource("");
        setProblemError("");
        setTitleEdited(false);
      }
    );
  }

  let totalSolved = 0;
  let totalQuestions = 0;
  const topicCard = questionData.map((topic, index) => {
    const { topicName, doneQuestions, questions, started } = topic;
    const total = questions.length;
    const percentDone = findPercentage(doneQuestions, total);
    totalSolved += doneQuestions;
    totalQuestions += total;
    const to = `/topic/${slugify(topicName)}`;
    const status = percentDone === 100 ? "done" : started ? "doing" : "todo";
    const statusLabel = status === "done" ? "Done" : status === "doing" ? "In progress" : "Not started";
    const pillClass = status === "done" ? "green" : status === "doing" ? "yellow" : "gray";
    return (
      <Link to={to} className="nb-card" key={index}>
        <div className="nb-card-top">
          <span className={`nb-card-icon ${status}`} role="img" aria-label="topic">
            {topicEmoji(topicName)}
          </span>
          {topic.Custom ? <span className="nb-pill blue nb-card-tag">custom</span> : null}
          {topic.Custom ? (
            <button
              type="button"
              className="nb-card-del"
              title="Delete custom topic"
              aria-label="delete topic"
              onClick={(e) => handleDeleteTopic(e, topic)}
            >
              <FiTrash2 size={14} />
            </button>
          ) : null}
        </div>
        <div className="nb-card-title">{topicName}</div>
        <div className="nb-card-meta">
          <span>
            {doneQuestions}/{total} solved
          </span>
          <span className={`nb-pill ${pillClass}`}>{statusLabel}</span>
        </div>
        <div className="nb-card-progress">
          <ProgressBar now={percentDone} />
        </div>
      </Link>
    );
  });

  const overallPct = totalQuestions ? Math.round((totalSolved / totalQuestions) * 100) : 0;

  return (
    <>
      <div className="page-header">
        <div className="page-emoji" role="img" aria-label="puzzle">
          🧩
        </div>
        <h1 className="page-title">Topics</h1>
        <p className="page-sub">
          {totalSolved
            ? `You've solved ${totalSolved} of ${totalQuestions} problems — ${overallPct}% of the way there.`
            : "Pick a topic and start solving. Your progress saves automatically on this device."}
        </p>
        {totalSolved ? (
          <div className="page-progress">
            <ProgressBar now={overallPct} />
          </div>
        ) : null}
      </div>

      {/* Big quick-nav tiles to the main areas of the app */}
      <div className="home-tiles">
        <Link to="/dashboard" className="home-tile">
          <span className="home-tile-emoji" role="img" aria-label="dashboard">
            📊
          </span>
          <div className="home-tile-body">
            <div className="home-tile-title">Dashboard</div>
            <div className="home-tile-sub">Progress, streak &amp; heatmap</div>
          </div>
          <FiArrowRight className="home-tile-arrow" size={20} />
        </Link>
        <Link to="/practice" className="home-tile">
          <span className="home-tile-emoji" role="img" aria-label="practice">
            🎯
          </span>
          <div className="home-tile-body">
            <div className="home-tile-title">Practice</div>
            <div className="home-tile-sub">One random problem + timer</div>
          </div>
          <FiArrowRight className="home-tile-arrow" size={20} />
        </Link>
        <Link to="/apply" className="home-tile">
          <span className="home-tile-emoji" role="img" aria-label="apply">
            💼
          </span>
          <div className="home-tile-body">
            <div className="home-tile-title">Apply</div>
            <div className="home-tile-sub">Track companies you apply to</div>
          </div>
          <FiArrowRight className="home-tile-arrow" size={20} />
        </Link>
      </div>

      <div className="nb-toolbar">
        <button type="button" className="nb-btn nb-btn-primary" onClick={openAddProblem}>
          <FiLink size={15} /> Add Problem
        </button>
        <button type="button" className="nb-btn" onClick={() => setShowAdd(true)}>
          <FiPlus size={15} /> Add Topic
        </button>
      </div>

      <div className="nb-grid">{topicCard}</div>

      {/* ADD TOPIC */}
      <Modal show={showAdd} onHide={() => setShowAdd(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add a custom topic</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              handleAddTopic();
            }}
          >
            <Form.Group>
              <Form.Label>Topic name</Form.Label>
              <Form.Control
                autoFocus
                value={newTopic}
                onChange={(e) => setNewTopic(e.target.value)}
                placeholder="e.g. Sliding Window"
              />
            </Form.Group>
            {addError ? <p style={{ color: "#dc3545", marginBottom: 0 }}>{addError}</p> : null}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAdd(false)}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleAddTopic}>
            Add topic
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ADD PROBLEM FROM A LINK */}
      <Modal show={showAddProblem} onHide={() => setShowAddProblem(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1.15rem" }}>🔗 Add a problem from a link</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              submitAddProblem();
            }}
          >
            <Form.Group>
              <Form.Label>
                Problem link{" "}
                {problemSource ? (
                  <span className="nb-pill blue" style={{ verticalAlign: "middle" }}>
                    {problemSource}
                  </span>
                ) : null}
              </Form.Label>
              <Form.Control
                autoFocus
                value={problemForm.URL}
                onChange={(e) => handleProblemUrlChange(e.target.value)}
                placeholder="Paste a LeetCode / GfG / Codeforces link…"
              />
              <Form.Text muted>The title is detected from the link — you can edit it below.</Form.Text>
            </Form.Group>
            <Form.Group>
              <Form.Label>Problem name *</Form.Label>
              <Form.Control
                value={problemForm.Problem}
                onChange={(e) => {
                  setTitleEdited(true);
                  setProblemForm({ ...problemForm, Problem: e.target.value });
                }}
                placeholder="e.g. Two Sum"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Topic *</Form.Label>
              <Form.Control
                as="select"
                value={problemForm.topicSlug}
                onChange={(e) => setProblemForm({ ...problemForm, topicSlug: e.target.value })}
              >
                {questionData.map((topic) => {
                  const s = slugify(topic.topicName);
                  return (
                    <option key={s} value={s}>
                      {topic.topicName}
                      {topic.Custom ? " (custom)" : ""}
                    </option>
                  );
                })}
              </Form.Control>
            </Form.Group>
            <Form.Group>
              <Form.Label>Difficulty</Form.Label>
              <Form.Control
                as="select"
                value={problemForm.Difficulty}
                onChange={(e) => setProblemForm({ ...problemForm, Difficulty: e.target.value })}
              >
                <option value="">— none —</option>
                <option value="Easy">Easy</option>
                <option value="Medium">Medium</option>
                <option value="Hard">Hard</option>
              </Form.Control>
            </Form.Group>
            <Form.Group>
              <Form.Label>Secondary link (optional)</Form.Label>
              <Form.Control
                value={problemForm.URL2}
                onChange={(e) => setProblemForm({ ...problemForm, URL2: e.target.value })}
                placeholder="editorial / another reference"
              />
            </Form.Group>
            {problemError ? <p style={{ color: "#dc3545", marginBottom: 0 }}>{problemError}</p> : null}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddProblem(false)}>
            Cancel
          </Button>
          <Button variant="success" onClick={submitAddProblem} disabled={!questionData.length}>
            Add problem
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
